/**
 * BYO v2 — probe output parsing.
 *
 * Turns the dry-run probe response into rows + schema + logs for the TestPanel.
 *
 * The probe sample can come back in several shapes depending on the connector:
 *   - A CSV string in `response` with columns rawMessage + nexlaMetaData
 *   - A JSON-encoded string in `response` with records/logs arrays
 *   - A plain object with records/logs/stdout fields directly
 *
 * Pure + unit-testable.
 */

import type { ByoSchemaField } from '../types';

/** Find the raw response payload wherever the stage nests it. */
function findResponse(output: any): any {
  return output?.output?.response ?? output?.response ?? output;
}

/** Try to JSON-parse a string; return null on failure. */
function tryParseJson(s: string): any {
  if (typeof s !== 'string') return null;
  const t = s.trim();
  if (t[0] !== '{' && t[0] !== '[') return null;
  try {
    return JSON.parse(t);
  } catch {
    return null;
  }
}

/** Parse one CSV line into fields (handles quoted fields + "" escapes). */
function parseCsvLine(line: string): string[] {
  const fields: string[] = [];
  let cur = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"') {
        if (line[i + 1] === '"') {
          cur += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        cur += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ',') {
      fields.push(cur);
      cur = '';
    } else {
      cur += ch;
    }
  }
  fields.push(cur);
  return fields;
}

/** Parse a Python-dict repr like {'id': '1', 'n': 2, 'x': None} into an object. */
function parsePyDict(s: string): Record<string, unknown> | null {
  if (!s || typeof s !== 'string') return null;
  const trimmed = s.trim();
  if (!trimmed.startsWith('{')) return null;
  try {
    const jsonish = trimmed
      .replace(/'/g, '"')
      .replace(/\bNone\b/g, 'null')
      .replace(/\bTrue\b/g, 'true')
      .replace(/\bFalse\b/g, 'false');
    return JSON.parse(jsonish);
  } catch {
    return null;
  }
}

/** Parse the CSV sample (rawMessage,nexlaMetaData) into record rows. */
function parseSampleCsv(text: string): Array<Record<string, unknown>> {
  if (typeof text !== 'string' || !/rawMessage/.test(text)) return [];
  const lines = text.split(/\r?\n/).filter((l) => l.trim().length > 0);
  if (lines.length < 2) return [];
  const header = parseCsvLine(lines[0]).map((h) => h.trim());
  const rawIdx = header.indexOf('rawMessage');
  if (rawIdx === -1) return [];
  const rows: Array<Record<string, unknown>> = [];
  for (let i = 1; i < lines.length; i++) {
    const cols = parseCsvLine(lines[i]);
    const rec = parsePyDict(cols[rawIdx]);
    if (rec) rows.push(rec);
    else if (cols[rawIdx]) rows.push({ rawMessage: cols[rawIdx] });
  }
  return rows;
}

/** Pull sample record rows from a probe stage response. */
export function extractRows(output: any): Array<Record<string, unknown>> {
  if (!output) return [];
  const resp = findResponse(output);

  // Resolve a JSON-encoded string in the response field.
  const respResolved = typeof resp === 'string' ? (tryParseJson(resp) ?? resp) : resp;

  // Array-shaped responses (some connectors/stages).
  const candidates = [
    respResolved?.output,
    respResolved?.records,
    respResolved?.samples,
    respResolved,
  ];
  for (const c of candidates) {
    if (Array.isArray(c)) {
      return c.map((row: any) =>
        row && typeof row === 'object' && 'rawMessage' in row ? row.rawMessage : row,
      );
    }
  }

  // CSV-string response (the BYO dry-run shape).
  if (typeof resp === 'string') return parseSampleCsv(resp);
  return [];
}

/**
 * Extract an inferred schema. Prefers an explicit schema field; otherwise
 * infers column names + JS types from the first parsed row.
 */
export function extractSchema(output: any): ByoSchemaField[] {
  const resp = findResponse(output);
  const respResolved = typeof resp === 'string' ? (tryParseJson(resp) ?? resp) : resp;
  const explicit = respResolved?.schema ?? output?.schema ?? respResolved?.output_schema;
  if (Array.isArray(explicit)) {
    return explicit.map((f: any) => ({
      name: String(f?.name ?? f?.field ?? ''),
      type: String(f?.type ?? f?.data_type ?? 'string'),
    }));
  }

  const rows = extractRows(output);
  if (rows.length && rows[0] && typeof rows[0] === 'object') {
    return Object.entries(rows[0]).map(([name, value]) => ({ name, type: jsTypeOf(value) }));
  }
  return [];
}

/**
 * Pull log lines from a probe response.
 * BYO Python stdout (print / nexla_utils.publish_*_log) lands in `stdout`.
 * Also checks logs/log/messages for other connectors.
 */
export function extractLogs(output: any): string[] {
  if (!output) return [];
  const resp = findResponse(output);
  const respResolved = typeof resp === 'string' ? (tryParseJson(resp) ?? resp) : resp;

  // stdout is the primary carrier for BYO Python print()/nexla_utils.publish_*_log output.
  const candidates = [
    output?.stdout,
    resp?.stdout,
    respResolved?.stdout,
    respResolved?.logs,
    output?.logs,
    resp?.logs,
    output?.log,
    resp?.log,
    resp?.messages,
    respResolved?.messages,
  ];

  for (const c of candidates) {
    if (Array.isArray(c)) return c.map((l: any) => (typeof l === 'string' ? l : JSON.stringify(l)));
    if (typeof c === 'string' && c.trim()) return c.split(/\r?\n/).filter(Boolean);
  }
  return [];
}

/**
 * Pull an error/exception message out of an otherwise-successful-looking
 * probe response. A Python exception in the user's `process()` can still
 * come back as HTTP 200 at the transport layer (the probe infrastructure
 * "succeeded" even though the code crashed) — `extractRows`/`extractLogs`
 * only recognize happy-path shapes, so that case silently rendered as an
 * empty result with no indication why (BYO pending-issues #5).
 *
 * The exact backend shape for this case hasn't been captured live yet, so
 * this checks the common conventions defensively (`error`/`exception`/
 * `traceback` keys, or a Python traceback/`FooError:` line in stdout)
 * rather than guessing one specific field name. Returns null — same as
 * today — for anything that doesn't match, so a genuinely successful run
 * is never affected. Narrow this once the real shape is confirmed live.
 */
export function extractError(output: any): string | null {
  if (!output) return null;
  const resp = findResponse(output);
  const respResolved = typeof resp === 'string' ? (tryParseJson(resp) ?? resp) : resp;

  const direct = [
    respResolved?.error,
    respResolved?.exception,
    resp?.error,
    resp?.exception,
    output?.error,
    output?.exception,
  ];
  for (const c of direct) {
    if (typeof c === 'string' && c.trim()) return c.trim();
    if (c && typeof c === 'object') {
      const msg = (c as any).message ?? (c as any).msg;
      if (msg) return String(msg);
    }
  }

  const traceback = respResolved?.traceback ?? resp?.traceback ?? output?.traceback;
  if (typeof traceback === 'string' && traceback.trim()) return traceback.trim();

  // Fall back to scanning stdout/logs for a Python traceback signature.
  const logLines = extractLogs(output);
  const tracebackIdx = logLines.findIndex((l) => /Traceback \(most recent call last\)/.test(l));
  if (tracebackIdx !== -1) return logLines.slice(tracebackIdx).join('\n');
  const errorLine = logLines.find((l) => /^[A-Za-z_][A-Za-z0-9_.]*Error:/.test(l.trim()));
  if (errorLine) return errorLine.trim();

  return null;
}

function jsTypeOf(value: unknown): string {
  if (value === null || value === undefined) return 'null';
  if (Array.isArray(value)) return 'array';
  if (typeof value === 'number') return Number.isInteger(value) ? 'integer' : 'number';
  return typeof value;
}
